import React, { useEffect, useState } from "react";
import Layout from "../../components/layout";
import { FaArrowLeft, FaPlus, FaSearch, FaCalendarAlt } from "react-icons/fa";
import Select from "react-select";
import { selectStyles } from "../../static/styles";

import styles from "./SamplesPage.module.scss";
import { useNavigate } from "react-router-dom";
import { route } from "../../static/route";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  fetchSamplesListData,
  getEditSample,
  getSamples,
  getTotalSamples,
  setEditSample,
  toggleSampleModal,
} from "../../features/samples/samplesSlice";
import Pagination from "../../components/pagination";
import { ISampleFormInput, OptionsProps } from "../../types/sample";
import { toast } from "react-toastify";
import { toastStyles } from "../../static/styles";
import { DELETE, POST, PUT } from "../../services/axios.service";
import { Controller, useForm } from "react-hook-form";
import SampleList from "../../components/samples/SampleList";
import SampleFormModal from "../../components/samples/SampleFormModal";

const SamplesPage = () => {
  const companyName = process.env.REACT_APP_COMPANY_NAME;

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const samples = useAppSelector(getSamples);
  const totalSamples = useAppSelector(getTotalSamples);
  const editSample = useAppSelector(getEditSample);

  interface FilterFormInput {
    name: string;
    fromDate: string;
    toDate: string;
    status: number;
  }

  const { handleSubmit, register, reset, getValues, control, setValue } =
    useForm<FilterFormInput>();

  const [page, setPage] = useState<number>(1);
  const [pageSize] = useState<number>(50);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showFilter, setShowFilter] = useState(false);
  const [unitOption] = useState<OptionsProps[]>([
    {
      value: 1,
      label: "વજન - KG",
    },
    {
      value: 2,
      label: "નંગ - Piece",
    },
    {
      value: 3,
      label: "જોડી - Pair",
    },
  ]);
  const [statusOption] = useState<OptionsProps[]>([
    {
      value: 0,
      label: "ચાલુ કામ",
    },
    {
      value: 1,
      label: "પૂરું થયેલું કામ",
    },
  ]);

  const hanldeGoToHome = () => {
    navigate(route.dashboard);
  };

  const paginate = (pageNumber: number) => {
    setPage(pageNumber);
  };

  const handleShowSampleForm = async () => {
    await dispatch(setEditSample(null));
    dispatch(toggleSampleModal(true));
  };

  const handleSaveSample = async (data: ISampleFormInput) => {
    try {
      if (editSample && editSample.id) {
        await PUT(`/v1/admin/sample-orders/${editSample.id}`, {
          qty: data.qty,
          delivered_qty: data.delivered_qty,
          unit: data.unit,
          amount: data.amount,
          status: data.status,
        })
          .then(async (response: any) => {
            if (response.data.status === "success") {
              await dispatch(setEditSample(null));

              if (page === 1) {
                fetchSamplesList(page, pageSize);
              } else {
                setPage(1);
              }

              toast.success(response.data.message, {
                ...(toastStyles as any),
              });
            } else {
              toast.error(response.data.message, {
                ...(toastStyles as any),
              });
            }
          })
          .catch((error: any) => {
            toast.error(error.data.message, {
              ...(toastStyles as any),
            });
          });
      } else {
        await POST("/v1/admin/sample-orders", {
          qty: data.qty,
          delivered_qty: data.delivered_qty,
          unit: data.unit,
          amount: data.amount,
          status: data.status,
        })
          .then(async (response: any) => {
            if (response.data.status === "success") {
              if (page === 1) {
                fetchSamplesList(page, pageSize);
              } else {
                setPage(1);
              }

              toast.success(response.data.message, {
                ...(toastStyles as any),
              });
            } else {
              toast.error(response.data.message, {
                ...(toastStyles as any),
              });
            }
          })
          .catch((error: any) => {
            toast.error(error.data.message, {
              ...(toastStyles as any),
            });
          });
      }
    } catch (error) {
      toast.error("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.", {
        ...(toastStyles as any),
      });
    }
  };

  const handleDeleteSample = async (id: number) => {
    try {
      await DELETE(`/v1/admin/sample-orders/${id}`)
        .then(async (response: any) => {
          if (response.data.status === "success") {
            const pageNumber = samples.length > 1 ? page : page - 1;
            if (page === pageNumber) {
              fetchSamplesList(page, pageSize);
            } else {
              setPage(pageNumber);
            }

            toast.success(response.data.message, {
              ...(toastStyles as any),
            });
          } else {
            toast.error(response.data.message, {
              ...(toastStyles as any),
            });
          }
        })
        .catch((error: any) => {
          toast.error(error.data.message, {
            ...(toastStyles as any),
          });
        });
    } catch (error) {
      toast.error("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.", {
        ...(toastStyles as any),
      });
    }
  };

  const handleOnFilterSubmit = async () => {
    fetchSamplesList(page, pageSize);
  };

  const handleClearFilter = () => {
    reset();
    fetchSamplesList(page, pageSize);
  };

  const fetchSamplesList = async (page: number, size: number) => {
    const data = getValues();

    setIsLoading(true);

    dispatch(
      fetchSamplesListData({
        page: page,
        size: size,
        search: data?.name || null,
        fromDate: data?.fromDate || null,
        toDate: data?.toDate || null,
        status: data?.status >= 0 ? data?.status : null,
      })
    ).then(() => {
      setIsLoading(false);
    });
  };

  useEffect(() => {
    fetchSamplesList(page, pageSize);
  }, [page]);

  return (
    <Layout>
      <div className={styles.SamplesPage}>
        <div className="logoSection">
          <h1
            onClick={hanldeGoToHome}
            className="text-4xl font-bold text-white lg:text-2xl cursor-pointer lg:w-fit"
          >
            {companyName}
          </h1>

          <hr className="w-[180px] border-white border-[4px] rounded-[4px] mx-auto mt-4 lg:mx-0 lg:w-24 lg:border-[2px] lg:mt-2" />
        </div>

        <div className="pageTitleSection">
          <FaArrowLeft
            onClick={hanldeGoToHome}
            className="lg:hidden absolute top-5 left-5 text-3xl text-white border-2 border-white p-1 rounded-full"
          />

          <h1 className="text-5xl mt-6 lg:mt-0 font-bold text-white lg:text-2xl lg:text-primary-1">
            સેમ્પલ ઓર્ડર ની વિગતો
          </h1>
        </div>

        <div className="mt-[60px] px-10 py-5">
          <div className="flex justify-between lg:justify-end gap-4 items-center mb-4">
            <button
              className="p-2 rounded-md flex items-center bg-gradient-to-r from-primary-1 to-primary-2 text-white transition-colors duration-300"
              onClick={() => setShowFilter(!showFilter)}
            >
              <FaSearch className="mr-2" />{" "}
              {showFilter ? "ફિલ્ટર છુપાવો" : "ફિલ્ટર બતાવો"}
            </button>
            {/* <button
              className="bg-gradient-to-r from-primary-1 to-primary-2 text-white p-2 rounded-md flex items-center transition-colors duration-300"
              onClick={handleShowSampleForm}
            >
              <FaPlus className="mr-2" /> નવો સેમ્પલ ઓર્ડર ઉમેરો
            </button> */}
          </div>

          {showFilter && (
            <form onSubmit={handleSubmit(handleOnFilterSubmit)}>
              <div className="mb-4 bg-white shadow rounded p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex items-center bg-gradient-to-r from-primary-1 to-primary-2 rounded pr-[2px] h-[44px]">
                    <FaCalendarAlt className="text-white mx-2" />
                    <input
                      className="bg-white text-primary-1 placeholder:text-primary-2 font-semibold text-xl focus:outline-none p-2 rounded flex-grow h-[40px]"
                      type="date"
                      {...register("fromDate")}
                      placeholder="From Date"
                    />
                  </div>

                  <div className="flex items-center bg-gradient-to-r from-primary-1 to-primary-2 rounded pr-[2px] h-[44px]">
                    <FaCalendarAlt className="text-white mx-2" />
                    <input
                      className="bg-white text-primary-1  placeholder:text-primary-2 font-semibold text-xl focus:outline-none p-2 rounded flex-grow h-[40px]"
                      type="date"
                      {...register("toDate")}
                      placeholder="To Date"
                    />
                  </div>

                  <div className="flex items-center bg-gradient-to-r from-primary-1 to-primary-2 rounded p-[2px] h-[44px]">
                    <input
                      {...register("name")}
                      className="text-primary-1 px-3 py-1 font-semibold text-xl placeholder:text-primary-2 w-full h-full rounded focus:outline-none"
                      placeholder="વિગતો દ્વારા શોધો"
                    />
                  </div>

                  <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 lg:h-12 w-full relative rounded">
                    <Controller
                      {...register("status")}
                      control={control}
                      render={({ field }) => (
                        <Select
                          isClearable
                          {...field}
                          value={
                            statusOption.find(
                              (val) => val.value === field.value
                            ) || null
                          }
                          className="text-primary-1 p-0 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                          styles={selectStyles}
                          placeholder={"સ્ટેટ્સ નું નામ સિલેક્ટ કરો"}
                          options={statusOption}
                          onChange={(val: any) =>
                            setValue("status", val?.value)
                          }
                        />
                      )}
                    />
                  </div>
                </div>
                <div className="flex gap-4 justify-end mt-4">
                  <button
                    onClick={handleClearFilter}
                    type="button"
                    className="bg-primary-1 hover:bg-primary-2 text-white px-4 py-2 rounded-md transition duration-300 ease-in-out"
                  >
                    હટાઓ
                  </button>

                  <button
                    type="submit"
                    className="bg-primary-1 hover:bg-primary-2 text-white px-4 py-2 rounded-md transition duration-300 ease-in-out"
                  >
                    ફિલ્ટર લાગુ કરો
                  </button>
                </div>
              </div>
            </form>
          )}

          <SampleList
            samples={samples}
            pageNumber={page}
            pageSize={pageSize}
            isLoading={isLoading}
            handleDeleteSample={handleDeleteSample}
            onPaymentsChanged={() => fetchSamplesList(page, pageSize)}
          />

          <Pagination
            itemsPerPage={pageSize}
            totalItems={totalSamples}
            paginate={paginate}
            currentPage={page}
          />
        </div>

        <SampleFormModal handleSaveSample={handleSaveSample} />
      </div>
    </Layout>
  );
};
export default SamplesPage;
